/**
 * Pure 1040 text → WizardInput parser.
 *
 * Internal pipeline:
 *   pdf text  →  extractTextRegex()  →  Parsed1040Return  →
 *                validate1040Return() (appends reconciliation warnings)
 *                                   →  mapToWizardInput()  →  Parsed1040 shape
 *
 * The Parsed1040Return intermediate type (in parse1040Types.ts) is the
 * architecture seam — future extractors (text-positional, IRS form-fields)
 * will plug in at the same point.
 *
 * Stays line-number based so it works across the 2018+ "postcard" format
 * that every tax software emits (TurboTax, Drake, Lacerte, ProConnect, etc.).
 */
import type { TaxScenarioInput, FilingStatus } from '@/tax/types';
import type {
  Parsed1040Return,
  ExtractedField,
  Confidence,
} from './parse1040Types';
import { mapToWizardInput } from './parse1040Map';
import { validate1040Return } from './parse1040Validate';

export interface Parsed1040 {
  fields: Partial<TaxScenarioInput>;
  warnings: string[];
  /** Combined joint W-2 wages the advisor must split on the import screen
   *  (no Schedule SE read for a business owner); null when not needed. */
  wageSplitTotal: number | null;
  /** Line items we successfully extracted, for the review UI. */
  extracted: Array<{ label: string; line: string; value: number | string }>;
  /** True if the document didn't look like a 1040 at all. */
  notRecognized: boolean;
  /** True if we got almost no text — likely a scanned PDF without a text layer. */
  likelyScanned: boolean;
}

/** Construct an ExtractedField with text-regex provenance. */
function field<T extends number | string | boolean>(
  value: T,
  sourceForm: string,
  sourceLineNumber: string,
  confidence: Confidence = 'medium',
): ExtractedField<T> {
  return { value, sourceForm, sourceLineNumber, confidence };
}

/** Strip $, commas, parens; turn (3,000) into -3000. Returns null on garbage. */
function parseAmount(raw: string | undefined | null): number | null {
  if (raw == null) return null;
  let s = raw.trim();
  if (!s) return null;
  // Parentheses → negative
  const negative = /^\(.*\)$/.test(s);
  if (negative) s = s.slice(1, -1);
  s = s.replace(/[$,\s]/g, '');
  if (s === '' || s === '-') return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return negative ? -Math.abs(n) : n;
}

/**
 * Look through the text for a logical line that contains the given line label
 * (e.g., "1a", "25a", "5b"), optionally requires a keyword on the same line for
 * disambiguation, and returns the rightmost currency-shaped token (the amount).
 *
 * This is more robust than a single anchored regex because tax software PDFs
 * vary in how they emit text — sometimes label and amount are split across
 * pdfjs text fragments separated by spaces, sometimes by newlines.
 */
/**
 * Debug helper: emits parser-internal traces when localStorage.debugParse1040 = '1'.
 * Used to diagnose extraction problems against real PDFs in the browser.
 */
function dbg(...args: unknown[]): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage?.getItem('debugParse1040') === '1') {
      // eslint-disable-next-line no-console
      console.log('[parse1040]', ...args);
    }
  } catch { /* no-op */ }
}

function findLineAmount(text: string, lineId: string, keyword?: string): number | null {
  dbg(`findLineAmount BEGIN line=${lineId} keyword=${keyword ?? '-'} textLen=${text.length} lines=${text.split(/\r?\n/).length}`);
  // Logical lines come from extractPdfText's y-clustering. Each line is one
  // visual row of the source form.
  const logicalLines = text.split(/\r?\n/);

  const lineIdRx = new RegExp(`(?:^|\\s)${lineId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$)`);
  // Currency-shaped tokens — must contain at least one digit; allow $, comma, paren, decimal
  const amountRx = /-?\(?\$?\d[\d,]*(?:\.\d+)?\)?|\(\$?\d[\d,]*(?:\.\d+)?\)/g;
  // Detector for "any line-marker-shaped token" — small numbers (1-99)
  // optionally followed by a single lowercase letter (1a, 5b, 25a, etc.).
  // Used to identify a row's primary line marker and to ignore rows where
  // lineId appears only as a description reference.
  const markerRx = /(?:^|\s)(\d{1,2}[a-z]?)(?=\s|$)/g;

  // Need a global flag to walk all matches per line.
  const globalLineIdRx = new RegExp(lineIdRx.source, lineIdRx.flags + 'g');

  // Bare-fallback across the whole scan: if NO row has a comma-formatted
  // candidate, return the first row's first bare candidate. This matters
  // when a line ID appears on multiple rows (e.g. Schedule E's instructional
  // "check line 28 and attach Form 6198" preceding the actual totals).
  let firstRowBareFallback: string | null = null;

  for (const line of logicalLines) {
    // Collect ALL line-marker-shaped tokens in this row. The row's "primary"
    // markers are the FIRST and LAST tokens — those are the IRS form's
    // left-margin and right-margin line indicators. Markers in between are
    // description references (e.g. "Combine lines 1a through 6 in column (h)"
    // on Schedule D line 7's row, or "Adjustments to income from Schedule 1,
    // line 26" on 1040 line 10's row).
    const rowMarkers: Array<{ token: string; index: number; length: number }> = [];
    markerRx.lastIndex = 0;
    let mm: RegExpExecArray | null;
    while ((mm = markerRx.exec(line))) {
      rowMarkers.push({ token: mm[1], index: mm.index, length: mm[0].length });
    }
    if (rowMarkers.length === 0) continue;

    const firstMarker = rowMarkers[0];
    let lastMarker = rowMarkers[rowMarkers.length - 1];
    // A value under 100 printed after the right-margin marker ("2a 0",
    // "7 50") is itself marker-shaped. When the row's last token is a bare
    // number sitting right after another marker, that number is the value
    // and the marker before it is the row's.
    if (rowMarkers.length > 1 && /^\d+$/.test(lastMarker.token)) {
      const prev = rowMarkers[rowMarkers.length - 2];
      const between = line.slice(prev.index + prev.length, lastMarker.index);
      const beforePrev = line.slice(Math.max(0, prev.index - 12), prev.index);
      const afterValue = line.slice(lastMarker.index + lastMarker.length);
      if (!between.trim() && !afterValue.trim() && !/\blines?\s*$/i.test(beforePrev)) lastMarker = prev;
    }

    // The row "belongs" to lineId only if lineId is the row's first OR last
    // primary marker. The first-marker test additionally rejects rows where
    // lineId is preceded by "line"/"lines" — those are description refs
    // mid-paragraph, not real line markers (e.g. "skip lines 4 through 12"
    // on Form 8995-A's instructional intro). The last-marker test stays
    // strict: a token at the right margin is reliably the line's value
    // indicator — and nothing but the amount follows it. Words after the
    // token mean it's a mid-sentence reference ("Add lines 4, 7 through 16,
    // 18, and 19" on Schedule 2 line 21 would otherwise read as line 7 with
    // a value of 16).
    const beforeFirst = line.slice(Math.max(0, firstMarker.index - 12), firstMarker.index);
    const isFirstMatch =
      firstMarker.token === lineId && !/\blines?\s*$/i.test(beforeFirst);
    const afterLast = line.slice(lastMarker.index + lastMarker.length);
    const isLastMatch = lastMarker.token === lineId && !/[A-Za-z]{2,}/.test(afterLast);
    if (!isFirstMatch && !isLastMatch) continue;

    if (keyword) {
      const kwRx = new RegExp(keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
      if (!kwRx.test(line)) continue;
    }

    // Find the LAST occurrence of lineId on this row. IRS forms put the
    // line marker IMMEDIATELY before the value column on the right, so
    // anchoring at the rightmost match scopes the value search correctly.
    // (The value 35,000 in "7 description ... 35,000" comes after the
    // single "7" at start. The 250,000 in "26 description ... 26 250,000"
    // comes after the second "26" at right margin.)
    const allLineIdMatches: Array<{ index: number; length: number }> = [];
    globalLineIdRx.lastIndex = 0;
    let lim: RegExpExecArray | null;
    while ((lim = globalLineIdRx.exec(line))) {
      allLineIdMatches.push({ index: lim.index, length: lim[0].length });
    }
    if (allLineIdMatches.length === 0) continue;
    const lastMatch = allLineIdMatches[allLineIdMatches.length - 1];

    const startAt = lastMatch.index + lastMatch.length;
    // Letter-suffix lineIds (2a, 5b, 25a) appear in paired-column layouts
    // ("2a desc 25,000   2b desc 18,000"). When looking for 2a, scope must
    // end before the SIBLING letter marker "2b" — otherwise an empty 2a
    // would mis-grab 2b's value. Bare-numeric lineIds (7, 15, 28) never
    // appear in paired columns on real IRS forms, so they scan to row end.
    let endAt = line.length;
    const letterMatch = lineId.match(/^(\d+)([a-z])$/);
    if (letterMatch) {
      const numPrefix = letterMatch[1];
      const ownLetter = letterMatch[2];
      const siblingRx = new RegExp(
        `(?:^|\\s)${numPrefix}([a-z])(?=\\s|$)`,
        'g',
      );
      siblingRx.lastIndex = startAt;
      let sm: RegExpExecArray | null;
      while ((sm = siblingRx.exec(line))) {
        if (sm[1] !== ownLetter) { endAt = sm.index; break; }
      }
    }
    const scopedText = line.slice(startAt, endAt);
    const matches = scopedText.match(amountRx);
    if (!matches) continue;

    const candidates = matches.filter(m => {
      if (m === lineId) return false;
      if (m.replace(/[^\d]/g, '') === lineId.replace(/[^\d]/g, '') && /^\d+$/.test(m)) return false;
      return parseAmount(m) !== null;
    });

    // Prefer comma-formatted candidates over bare digits. The IRS form
    // template embeds bare dollar amounts in instructional text (e.g.
    // "If you made any gift of $250 or more, see instructions" next to
    // Schedule A line 11) and form-number references (e.g. "Form 6198").
    // The actual overlaid value is comma-formatted ("50,000", "620,000").
    const commaFormatted = candidates.filter(c => c.includes(','));

    dbg(`findLineAmount line=${lineId}`, {
      keyword,
      lineExcerpt: line.length > 200 ? line.slice(0, 200) + '…' : line,
      scopedExcerpt: scopedText.length > 100 ? scopedText.slice(0, 100) + '…' : scopedText,
      candidates,
      commaFormatted,
    });

    // If this row has a comma-formatted candidate, return immediately —
    // that's the actual money value.
    if (commaFormatted.length > 0) {
      return parseAmount(commaFormatted[0]);
    }
    // No comma-formatted candidate. Consider the LAST bare candidate as a
    // fallback only if it sits at the END of the row (after stripping
    // trailing dots/whitespace). On real IRS forms the value column is on
    // the right margin, so a bare value at row-end is the actual entry —
    // whereas a bare amount in the middle of a description ("If you made
    // any gift of $250 or more, see instructions") is template text.
    if (firstRowBareFallback === null && candidates.length > 0) {
      const last = candidates[candidates.length - 1];
      const trimmedScope = scopedText.replace(/[\s.]+$/, '');
      if (trimmedScope.endsWith(last)) {
        firstRowBareFallback = last;
      }
    }
  }

  if (firstRowBareFallback != null) {
    dbg(`findLineAmount END line=${lineId} bare-fallback=${firstRowBareFallback}`);
    return parseAmount(firstRowBareFallback);
  }
  dbg(`findLineAmount END line=${lineId} NO_MATCH after scanning ${logicalLines.length} lines`);
  return null;
}

function detectFilingStatus(text: string): FilingStatus | null {
  // Look for the checkbox section near the top. Different software marks the chosen
  // box differently — "X", "[X]", "☒", or just bolds the label. We pick the status
  // whose label appears nearest a check-mark indicator.
  const upper = text.toUpperCase();

  // Heuristic 1: explicit "Filing Status: Married Filing Jointly" string (common in
  // tax software cover pages and footers). REQUIRES a colon to avoid matching
  // the IRS form's "Filing Status Check only one box. Single Married filing
  // jointly..." which would always return SINGLE (the first option).
  const explicit = upper.match(/FILING\s*STATUS\s*:\s*(SINGLE|MARRIED\s+FILING\s+JOINTLY|MARRIED\s+FILING\s+SEPARATELY|HEAD\s+OF\s+HOUSEHOLD|QUALIFYING\s+(?:SURVIVING\s+SPOUSE|WIDOW))/);
  if (explicit) {
    const s = explicit[1].replace(/\s+/g, ' ');
    if (s.startsWith('SINGLE')) return 'S';
    if (s.startsWith('MARRIED FILING JOINTLY')) return 'MFJ';
    if (s.startsWith('MARRIED FILING SEPARATELY')) return 'MFS';
    if (s.startsWith('HEAD OF HOUSEHOLD')) return 'HOH';
    if (s.startsWith('QUALIFYING')) return 'MFJ'; // QSS uses MFJ brackets
  }

  // Heuristic 2: checkbox marker [X] / ☒ next to the label.
  const checkMarker = /(?:\[\s*[Xx]\s*\]|☒|✓|✔|■)\s*([A-Z][A-Za-z ]{3,40})/g;
  let m: RegExpExecArray | null;
  while ((m = checkMarker.exec(text))) {
    const label = m[1].toUpperCase().trim();
    if (label.startsWith('SINGLE')) return 'S';
    if (label.startsWith('MARRIED FILING JOINTLY')) return 'MFJ';
    if (label.startsWith('MARRIED FILING SEPARATELY')) return 'MFS';
    if (label.startsWith('HEAD OF HOUSEHOLD')) return 'HOH';
  }

  // Heuristic 2b: naked "X" immediately preceding a filing-status label.
  // Tax-software-generated PDFs frequently emit the marker as just "X"
  // (no brackets, no special glyph) — e.g. "Check only X Married filing
  // jointly". A naked X is risky to match in general because random caps
  // appear in body text, so we anchor it directly to the filing-status
  // label phrases. No false-positive surface.
  const nakedX = upper.match(
    /\bX\s+(SINGLE|MARRIED\s+FILING\s+JOINTLY|MARRIED\s+FILING\s+SEPARATELY|HEAD\s+OF\s+HOUSEHOLD|QUALIFYING\s+(?:SURVIVING\s+SPOUSE|WIDOW))\b/,
  );
  if (nakedX) {
    const s = nakedX[1].replace(/\s+/g, ' ');
    if (s.startsWith('SINGLE')) return 'S';
    if (s.startsWith('MARRIED FILING JOINTLY')) return 'MFJ';
    if (s.startsWith('MARRIED FILING SEPARATELY')) return 'MFS';
    if (s.startsWith('HEAD OF HOUSEHOLD')) return 'HOH';
    if (s.startsWith('QUALIFYING')) return 'MFJ'; // QSS uses MFJ brackets
  }

  // Heuristic 3: infer MFJ from a populated spouse SSN in the header area.
  // The 1040 has a "Spouse's social security number" column that's filled
  // ONLY for MFJ (and rarely MFS). After y-clustering, values often land
  // ABOVE their column-header labels, so we can't anchor on the label —
  // instead, count SSN-shaped tokens in the first 3,000 chars (header
  // region, before income lines). Two SSNs ⇒ joint return; one ⇒ single.
  // Defaulting to MFJ when ambiguous is the right call: an advisor will
  // catch a wrong MFJ much faster than a wrong "filing status not detected"
  // (which silently leaves the wizard's status field blank).
  const headerArea = text.slice(0, 3000);
  const ssnRx = /(?:\d{3}|XXX|\*{3})[\s-]?(?:\d{2}|XX|\*{2})[\s-]?\d{4}/g;
  const ssnMatches = headerArea.match(ssnRx) ?? [];
  // De-dup: pdfjs sometimes splits "412-55-9183" into multiple text items
  // that recombine differently. Counting unique SSN strings is sturdier.
  const uniqueSsns = new Set(ssnMatches.map(s => s.replace(/[\s-]/g, '')));
  if (uniqueSsns.size >= 2) return 'MFJ';

  return null;
}

function detectTaxYear(text: string): number | null {
  // "Form 1040 ... 20YY" or "U.S. Individual Income Tax Return 20YY"
  const m = text.match(/(?:Form\s*1040[^\n]{0,80}|Tax\s*Return[^\n]{0,40})(20\d{2})/i);
  if (m) return parseInt(m[1], 10);
  // Fallback: any 20YY in the first 500 chars
  const head = text.slice(0, 500);
  const yr = head.match(/\b(20\d{2})\b/);
  return yr ? parseInt(yr[1], 10) : null;
}

const US_STATES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL',
  'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT',
  'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
]);

/**
 * Find a "<STATE> <ZIP>" pattern in the address section.
 * Anchoring on the ZIP-shaped suffix avoids false positives — random "MA"
 * or "OR" tokens in body text won't be followed by a 5-digit ZIP.
 */
function detectState(text: string): string | null {
  const rx = /\b([A-Z]{2})\s+(\d{5})(?:-\d{4})?\b/g;
  let m: RegExpExecArray | null;
  while ((m = rx.exec(text))) {
    if (US_STATES.has(m[1])) return m[1];
  }
  return null;
}

/**
 * Detect the age-65 checkboxes on 1040 line 12d. The real 2025 form prints
 * the label, then the box, then the text:
 *   "You: [box] Were born before January 2, 1961 [box] Are blind"
 *   "Spouse: [box] Was born before January 2, 1961 [box] Is blind"
 * so a box is checked only when a mark ([X], X, ☒, ✓, ✔, ■) sits between
 * the label and "born before". A mark after that belongs to the blind box.
 */
function detectAge65(text: string): { tp65Plus?: boolean; sp65Plus?: boolean } {
  const out: { tp65Plus?: boolean; sp65Plus?: boolean } = {};
  const rowRx = /\b(You|Spouse):([^\n\r]{0,12}?)W(?:ere|as)\s+born\s+before/gi;
  const markRx = /\[\s*[Xx]\s*\]|☒|✓|✔|■|(?:^|\s)[Xx](?=\s|$)/;
  let m: RegExpExecArray | null;
  while ((m = rowRx.exec(text))) {
    if (!markRx.test(m[2])) continue;
    if (m[1].toLowerCase() === 'spouse') out.sp65Plus = true;
    else out.tp65Plus = true;
  }
  return out;
}

/**
 * Pull taxpayer (and spouse) names from the 1040 header.
 * Form labels "Your first name and middle initial" and "Last name" appear
 * just before the entered values in pdfjs text-order. SSN-shaped tokens
 * (XXX-XX-XXXX, XXX XX XXXX, masked variants) act as a reliable delimiter
 * between the name fields and the next form section.
 *
 * SSN values are deliberately discarded — never written into wizard input.
 */
function detectNames(text: string, isJoint: boolean): {
  taxpayerFirst?: string;
  taxpayerLast?: string;
  spouseFirst?: string;
  spouseLast?: string;
} {
  const out: ReturnType<typeof detectNames> = {};
  const ssnRx = /(?:\d{3}|XXX|\*{3})[\s-]?(?:\d{2}|XX|\*{2})[\s-]?\d{4}/;

  const tpAnchor = text.search(/Your\s+first\s+name\s+and\s+middle\s+initial/i);
  if (tpAnchor < 0) {
    dbg('detectNames: no "Your first name" anchor found');
    return out;
  }
  const afterAnchor = text.slice(tpAnchor);
  const ssnMatch = afterAnchor.match(ssnRx);
  if (!ssnMatch || ssnMatch.index == null) {
    dbg('detectNames: no SSN delimiter found after taxpayer-name anchor');
    return out;
  }

  const block = afterAnchor.slice(0, ssnMatch.index)
    .replace(/Your\s+first\s+name\s+and\s+middle\s+initial/i, '')
    .replace(/Last\s+name/i, '')
    .replace(/Your\s+social\s+security\s+number/i, '')
    .trim();

  const tokens = block.match(/[A-Z][A-Za-z'.-]+/g) || [];
  dbg('detectNames taxpayer block', { rawBlock: block.slice(0, 300), tokens });
  if (tokens.length >= 2) {
    if (tokens.length === 3 && tokens[1].replace('.', '').length === 1) {
      out.taxpayerFirst = `${tokens[0]} ${tokens[1]}`;
      out.taxpayerLast = tokens[2];
    } else {
      out.taxpayerFirst = tokens[0];
      out.taxpayerLast = tokens[tokens.length - 1];
    }
  }

  if (!isJoint) return out;

  // The IRS template prints "spouse’s" with a curly apostrophe.
  const spAnchor = text.search(/(?:If\s+joint\s+return,?\s+)?[Ss]pouse['’]?s?\s+first\s+name\s+and\s+middle\s+initial/);
  if (spAnchor < 0) return out;
  const afterSp = text.slice(spAnchor);
  const spSsnMatch = afterSp.match(ssnRx);
  if (!spSsnMatch || spSsnMatch.index == null) return out;

  const spBlock = afterSp.slice(0, spSsnMatch.index)
    .replace(/(?:If\s+joint\s+return,?\s+)?[Ss]pouse['’]?s?\s+first\s+name\s+and\s+middle\s+initial/, '')
    .replace(/Last\s+name/i, '')
    .replace(/[Ss]pouse['’]?s?\s+social\s+security\s+number/, '')
    .trim();

  const spTokens = spBlock.match(/[A-Z][A-Za-z'.-]+/g) || [];
  if (spTokens.length >= 2) {
    if (spTokens.length === 3 && spTokens[1].replace('.', '').length === 1) {
      out.spouseFirst = `${spTokens[0]} ${spTokens[1]}`;
      out.spouseLast = spTokens[2];
    } else {
      out.spouseFirst = spTokens[0];
      out.spouseLast = spTokens[spTokens.length - 1];
    }
  }

  return out;
}

/**
 * Format the wizard's free-text "Client Name" field from parsed parts.
 * - Single / HOH / MFS → "First Last"
 * - MFJ same last name → "First & SpouseFirst Last"
 * - MFJ different last names → "First Last & SpouseFirst SpouseLast"
 */
function formatClientName(
  isJoint: boolean,
  parts: ReturnType<typeof detectNames>,
): string | null {
  const { taxpayerFirst, taxpayerLast, spouseFirst, spouseLast } = parts;
  if (!taxpayerFirst || !taxpayerLast) return null;
  if (!isJoint || !spouseFirst || !spouseLast) {
    return `${taxpayerFirst} ${taxpayerLast}`;
  }
  if (taxpayerLast === spouseLast) {
    return `${taxpayerFirst} & ${spouseFirst} ${taxpayerLast}`;
  }
  return `${taxpayerFirst} ${taxpayerLast} & ${spouseFirst} ${spouseLast}`;
}

/**
 * Generic helper: given a header marker (e.g. "SCHEDULE A", "FORM 8995-A"),
 * return the slice of text from that header up to the next form/schedule
 * header. Used so each schedule parser only sees its own section's text
 * and can't accidentally pick up labels from neighboring forms.
 */
function findSection(text: string, headerRx: RegExp): string | null {
  const m = text.match(headerRx);
  if (!m || m.index == null) {
    dbg(`findSection MISS pattern=${headerRx}`);
    return null;
  }
  const start = m.index;
  const tail = text.slice(start);
  // Boundary = the next "OMB No. NNNN-NNNN" marker. This appears at the
  // top of every IRS form/schedule and NEVER in body text — making it the
  // most reliable cross-section delimiter. Skip past the section's OWN
  // OMB (always within the first ~200 chars of an IRS form header) so we
  // don't terminate the section at its own header. If the section has no
  // OMB (synthetic test fixtures), fall back to a small skip to clear the
  // section anchor itself.
  const ombRx = /OMB\s*No\.?\s*\d{4}-\d{4}/gi;
  ombRx.lastIndex = 0;
  const ownOmb = ombRx.exec(tail);
  const SKIP = ownOmb && ownOmb.index < 250
    ? ownOmb.index + ownOmb[0].length
    : Math.min(50, tail.length);
  const remainder = tail.slice(SKIP);
  const nextRx = /OMB\s*No\.?\s*\d{4}-\d{4}/i;
  const nextM = remainder.match(nextRx);
  const sectionLen = nextM && nextM.index != null ? nextM.index + SKIP : tail.length;
  const section = tail.slice(0, sectionLen);
  dbg(`findSection HIT pattern=${headerRx}`, {
    headerStart: start,
    matchedHeader: m[0].slice(0, 100),
    sectionLength: section.length,
    first300: section.slice(0, 300),
  });
  return section;
}

/**
 * Find ALL sections matching a header (e.g. multiple Schedule C filings,
 * one for the taxpayer and one for the spouse). Returns sections in the
 * order they appear.
 */
function findAllSections(text: string, headerRx: RegExp): string[] {
  const sections: string[] = [];
  // Need a global flag to walk every match.
  const globalHeader = new RegExp(headerRx.source, headerRx.flags.includes('g') ? headerRx.flags : headerRx.flags + 'g');
  let lastEnd = 0;
  const matches: number[] = [];
  let m: RegExpExecArray | null;
  while ((m = globalHeader.exec(text))) {
    matches.push(m.index);
    if (m.index === lastEnd) globalHeader.lastIndex++;
    lastEnd = m.index;
  }
  for (let i = 0; i < matches.length; i++) {
    const start = matches[i];
    const end = i + 1 < matches.length ? matches[i + 1] : text.length;
    sections.push(text.slice(start, end));
  }
  return sections;
}

/**
 * Parse Schedule A itemized-deduction lines if present in the same document.
 * Many tax software PDFs include the entire return; the Schedule A section
 * starts with "SCHEDULE A" and has its own line numbering.
 */
function parseScheduleA(text: string): Partial<TaxScenarioInput> & { mortgageNotOn1098?: number } {
  const out: Partial<TaxScenarioInput> & { mortgageNotOn1098?: number } = {};
  // Require the form's title nearby so body references like "see Schedule A"
  // don't trigger section detection.
  const section = findSection(text, /Schedule\s*A\b[\s\S]{0,300}?Itemized\s+Deductions/i);
  if (!section) return out;

  // No keywords — within Schedule A section, line IDs are unambiguous.
  const med = findLineAmount(section, '1');
  if (med != null) out.medicalExpenses = med;

  const salt5a = findLineAmount(section, '5a');
  if (salt5a != null) out.saltStateLocal = salt5a;

  const salt5b = findLineAmount(section, '5b');
  if (salt5b != null) out.saltRealEstate = salt5b;

  const salt5c = findLineAmount(section, '5c');
  if (salt5c != null) out.saltPersonalProperty = salt5c;

  const mort = findLineAmount(section, '8a');
  if (mort != null) out.homeMortgageInterest = mort;

  const invInt = findLineAmount(section, '9');
  if (invInt != null) out.investmentInterest = invInt;

  const cashGift = findLineAmount(section, '11');
  if (cashGift != null) out.cashGiftsPublicCharity = cashGift;

  const nonCash = findLineAmount(section, '12');
  if (nonCash != null) out.nonCashPropertyPublicCharity = nonCash;

  const mort8b = findLineAmount(section, '8b');
  if (mort8b != null) out.mortgageNotOn1098 = mort8b;

  const points = findLineAmount(section, '8c');
  if (points != null) out.mortgagePoints = points;

  const other = findLineAmount(section, '16');
  if (other != null) out.otherItemizedDeductions = other;

  return out;
}

/** Schedule 1 Part II line → Parsed1040Return key. Line 22 is reserved,
 *  24a-24z roll up into 25, and 26 is the total the mapper checks against
 *  1040 line 10, so none of those is read. */
const SCHEDULE_1_LINES: Array<[string, keyof Parsed1040Return]> = [
  ['11', 'sch1Educator'],
  ['12', 'sch1BusinessExpenses'],
  ['13', 'sch1HSA'],
  ['14', 'sch1MovingExpenses'],
  ['15', 'sch1DeductibleSETax'],
  ['16', 'sch1SERetirement'],
  ['17', 'sch1SEHealthInsurance'],
  ['18', 'sch1EarlyWithdrawalPenalty'],
  ['19a', 'sch1Alimony'],
  ['20', 'sch1IRA'],
  ['21', 'sch1StudentLoan'],
  ['23', 'sch1ArcherMSA'],
  ['25', 'sch1OtherAdjustments'],
];

/**
 * Parse Schedule 1 Part II — Adjustments to Income (lines 11-25). Part I
 * (additional income) is already covered by 1040 line 8 and the Schedule
 * C/E parsers, so only Part II is read.
 */
function parseSchedule1(text: string): Map<string, number> {
  const out = new Map<string, number>();
  const section = findSection(text, /Schedule\s*1\b[\s\S]{0,300}?Additional\s+Income\s+and\s+Adjustments\s+to\s+Income/i);
  if (!section) return out;
  // Start at Part II. The schedule's own header row carries the year as
  // "20 25", which would otherwise read as lines 20 and 25.
  const partII = section.search(/Part\s*II\s+Adjustments\s+to\s+Income/i);
  if (partII < 0) return out;
  const body = section.slice(partII);
  for (const [line] of SCHEDULE_1_LINES) {
    const amount = findLineAmount(body, line);
    if (amount != null) out.set(line, amount);
  }
  return out;
}

/**
 * Result type for Schedule C — a return can have multiple Schedule C filings
 * (taxpayer + spouse on MFJ). We return one entry per filing in document order
 * and the caller assigns to taxpayer vs. spouse fields.
 */
interface ScheduleCFiling {
  grossReceipts?: number;
  totalExpenses?: number;
  /** True when expenses were set from line 31 (line 1 − line 31). */
  fromNetProfit?: boolean;
}

/**
 * Parse Schedule C — Profit or Loss from Business.
 * Line 1: Gross receipts or sales
 * Line 28: Total expenses (interim total, before home office)
 * Line 31: Net profit or loss (used as fallback if 1/28 not parseable)
 *
 * Returns one entry per Schedule C found. MFJ returns can have two.
 */
function parseScheduleC(text: string): ScheduleCFiling[] {
  // Require the form's title near the form number — IRS Schedule C says
  // "Profit or Loss From Business" right beneath the schedule label.
  // Without this constraint, references on the 1040 ("from Schedule C")
  // and footer text were creating false section matches.
  const sections = findAllSections(text, /Schedule\s*C\b[\s\S]{0,300}?Profit\s+or\s+Loss/i);
  return sections.map(section => {
    const filing: ScheduleCFiling = {};
    // No keyword — within a Schedule C section, "1" and "28" are unambiguous.
    const grossReceipts = findLineAmount(section, '1');
    if (grossReceipts != null) filing.grossReceipts = grossReceipts;
    const totalExpenses = findLineAmount(section, '28');
    if (totalExpenses != null) filing.totalExpenses = totalExpenses;
    // Line 31 is the business's net profit after returns, cost of goods,
    // other income and home office, none of which line 28 carries. The
    // engine (like v32's Schedule C!B31) only uses receipts minus expenses,
    // so expenses are set to make that difference equal line 31.
    const netProfit = findLineAmount(section, '31');
    if (netProfit != null) {
      if (filing.grossReceipts != null && filing.grossReceipts >= netProfit) {
        filing.totalExpenses = filing.grossReceipts - netProfit;
      } else {
        filing.grossReceipts = netProfit;
        filing.totalExpenses = 0;
      }
      filing.fromNetProfit = true;
    }
    return filing;
  });
}

/**
 * Parse Schedule SE — Self-Employment Tax. One per person with business
 * income, in the same document order as Schedule C ([0] taxpayer, [1] spouse).
 * Line 8a is that person's own W-2 Social Security wages (boxes 3 + 7), the
 * one number self-employment tax needs and the 1040 never splits by person.
 *
 * Returns, per filing, the line 8a amount or null when the line is blank.
 * Anything under $1,000 is treated as blank: the line's own description
 * carries bare digits ("boxes 3 and 7", "W-2") that the generic row reader
 * can fall back to when the value box is empty.
 */
function parseScheduleSE(text: string): Array<number | null> {
  // The real form prints "SCHEDULE SE  OMB No. 1545-0074" on one row and
  // "(Form 1040)  Self-Employment Tax" on the next. "Self-Employment Tax" has
  // to follow "(Form 1040)" directly (a year may sit between), so the page
  // footer ("Schedule SE (Form 1040) 2025  Created …") and page 2's header
  // ("… 2025  Page 2") never start a second filing.
  const headerRx = /Schedule\s+SE\b(?:\s*OMB\s*No\.?\s*[\d-]+)?\s*\(Form\s*1040\)\s*(?:\d{2}\s*\d{2}\s*)?Self-?\s*Employment\s+Tax/i;
  const globalRx = new RegExp(headerRx.source, 'gi');
  const starts: number[] = [];
  let m: RegExpExecArray | null;
  while ((m = globalRx.exec(text))) starts.push(m.index);
  return starts.map(start => {
    // Bound each filing at the next form's OMB marker, so a later form's
    // "8a" (Schedule A mortgage interest) can never be read as this one's.
    const section = findSection(text.slice(start), headerRx) ?? '';
    const v = findLineAmount(section, '8a');
    return v != null && v >= 1000 ? v : null;
  });
}

/**
 * Parse Schedule D — Capital Gains and Losses.
 * Line 7  (Part I):  Net short-term capital gain or (loss)
 * Line 15 (Part II): Net long-term capital gain or (loss)
 *
 * The wizard splits cap gains into short-term and long-term for proper
 * preferential-rate stacking. Without Schedule D we fall back to lumping
 * 1040 line 7 as long-term — Schedule D lets us do it properly.
 */
function parseScheduleD(text: string): { shortTerm?: number; longTerm?: number } {
  const out: { shortTerm?: number; longTerm?: number } = {};
  const section = findSection(text, /Schedule\s*D\b[\s\S]{0,300}?Capital\s+Gains/i);
  if (!section) return out;
  // No keyword — within the Schedule D section, "7" and "15" are unambiguous.
  // Adding "short-term" / "long-term" keyword gates broke matching when pdfjs
  // emitted hyphenated terms as separate tokens or when the line marker
  // landed on a different y-row from the description after clustering.
  const stcg = findLineAmount(section, '7');
  if (stcg != null) out.shortTerm = stcg;
  const ltcg = findLineAmount(section, '15');
  if (ltcg != null) out.longTerm = ltcg;
  return out;
}

/**
 * Parse Schedule E — Supplemental Income and Loss.
 * Part I (Rental Real Estate):
 *   Line 3:  Rents received
 *   Line 20: Total expenses
 * Part II (K-1: Partnerships and S Corporations) line entries vary; we pull
 * the most common K-1 box equivalents that surface on Schedule E.
 */
function parseScheduleE(text: string): {
  rentalIncome?: number;
  rentalExpenses?: number;
  k1Ordinary?: number;
  k1Guaranteed?: number;
  k1RentalRealEstate?: number;
  k1Other?: number;
} {
  const out: ReturnType<typeof parseScheduleE> = {};
  const section = findSection(text, /Schedule\s*E\b[\s\S]{0,300}?Supplemental\s+Income/i);
  if (!section) return out;

  // Prefer the IRS's own per-property totals (lines 23a, 23e) over the raw
  // per-column lines 3 and 20. Schedule E Part I lays out properties in
  // columns A/B/C; line 3's row carries up to three rent values side by side,
  // and findLineAmount only returns the FIRST comma-formatted value — so on a
  // multi-property return the parser silently dropped property B and C. Lines
  // 23a and 23e are the totals rows the IRS prints below the per-property
  // grid: "Total of all amounts reported on line 3 / line 20 for all rental
  // properties." Even on a single-property return the totals rows are filled
  // in (they're required), so 23a/23e is a strict superset of the prior
  // behavior. Fall back to line 3/20 only when 23a/23e aren't present (older
  // form layouts or non-standard vendors).
  const rentalIncome = findLineAmount(section, '23a') ?? findLineAmount(section, '3');
  if (rentalIncome != null) out.rentalIncome = rentalIncome;
  const rentalExpenses = findLineAmount(section, '23e') ?? findLineAmount(section, '20');
  if (rentalExpenses != null) out.rentalExpenses = rentalExpenses;

  // Part II K-1 totals. Line 32 is the canonical "Total partnership and S
  // corporation income or (loss)" — a single-value full-width row. Line 28
  // is the per-entry header row that appears multiple times (instructional
  // references, the column-header row, and individual entries A-D), so it's
  // unreliable. Try 32 first; fall back to 28 only when 32 isn't present
  // (synthetic test fixtures that emit a flat "28 ... 100,000" line).
  const k1Ordinary = findLineAmount(section, '32') ?? findLineAmount(section, '28');
  if (k1Ordinary != null) out.k1Ordinary = k1Ordinary;

  const k1Guaranteed = findLineAmount(section, '28', 'guaranteed');
  if (k1Guaranteed != null) out.k1Guaranteed = k1Guaranteed;

  return out;
}

/**
 * Parse Form 8995-A — Qualified Business Income Deduction (the "long form"
 * QBI worksheet used when income exceeds the 8995 simplified-form threshold).
 *
 * Part I:    Trade, business, or aggregation information — includes SSTB Y/N.
 * Part II:
 *   Line 4:  Allocable share of W-2 wages from the qualified trade or business
 *   Line 7:  Allocable share of UBIA of qualified property
 *
 * NOTE: line 9 is "Add lines 6 and 8" — a computed subtotal, NOT UBIA.
 * UBIA is line 7. (Verified against the 2025 Form 8995-A.)
 *
 * These three values are exactly what the wizard's Business step needs.
 */
function parseForm8995A(text: string): {
  qbiW2Wages?: number;
  qbiUBIA?: number;
  qbiSSTB?: boolean;
} {
  const out: ReturnType<typeof parseForm8995A> = {};
  // Match the form header specifically (form number + title in close
  // proximity) — NOT the line 13 reference on the 1040 ("Qualified
  // business income deduction from Form 8995 or Form 8995-A") which has
  // "Form 8995-A" but NOT followed by the title within 50 chars.
  const section = findSection(text, /Form\s*8995-?A\b[\s\S]{0,300}?Qualified\s+Business\s+Income\s+Deduction/i);
  if (!section) return out;

  // No keywords — within 8995-A section, "4" and "7" are unambiguous.
  const w2 = findLineAmount(section, '4');
  if (w2 != null) out.qbiW2Wages = w2;

  // UBIA is line 7. (Line 9 is "Add lines 6 and 8", a subtotal — not UBIA.)
  const ubia = findLineAmount(section, '7');
  if (ubia != null) out.qbiUBIA = ubia;

  // SSTB detection. Part I has a "(b) Check if specified service" column
  // on every 8995-A (so "specified service" alone isn't a signal). Real
  // tax software typically writes the trade name with "(SSTB)" parenthetical
  // when the business is a specified service trade — and a checkbox marker
  // ([X], ☒, ✓) sits to the left of the trade name on the same row. Either
  // signal in the Part I block (first ~800 chars to cover the wider Part I
  // section that pdfjs y-clusters can spread across) marks SSTB.
  const partI = section.slice(0, 800);
  // "(SSTB)" appears in two contexts on real forms: as a trade-name suffix
  // ("Hernandez Wealth Advisory (SSTB)" = a positive marker) and as a label
  // for a Yes/No checkbox question ("Specified service trade or business
  // (SSTB)?" = neutral header text). The negative lookahead for "?" rules
  // out the question header.
  const sstbInTradeName = /\(\s*SSTB\s*\)(?!\s*\?)/i.test(partI);
  const sstbMarker = /(?:\[\s*[Xx]\s*\]|☒|✓|✔|■)\s*Yes/i;
  const sstbCheckedExplicitly = sstbMarker.test(partI) && /SSTB|specified service/i.test(partI);
  if (sstbInTradeName || sstbCheckedExplicitly) {
    out.qbiSSTB = true;
  }

  return out;
}

/**
 * Parse Form 6251 — Alternative Minimum Tax (Individuals).
 * Line 1b: Subtract line 1a from 1040 line 11b (the AMT regular-tax base)
 * Line 2:  "Adjustments and preferences" — header only, no aggregate
 *          value column on real IRS forms (sub-lines 2a-2t carry values).
 * Line 4:  Alternative minimum taxable income (AMTI) — single full-width
 *          totals row, the most reliable signal on real returns.
 *
 * The wizard's `amtAdjustment` is the engine input that gets ADDED to
 * regular taxable income to derive AMTI: amti = taxableIncome + adjustments.
 * So if we have AMTI and line 1b, the adjustment is their difference.
 *
 * Returns whichever fields can be extracted; the mapper composes
 * amtAdjustment from line4 - line1b when both are available, else falls
 * back to the legacy line "2" lookup (which works for synthetic test
 * fixtures that emit a flat "2 ... 25,000" line).
 */
function parseForm6251(text: string): {
  amtAdjustment?: number;
  amti?: number;
  amtRegularBase?: number;
  line2a?: number;
} {
  const out: ReturnType<typeof parseForm6251> = {};
  // Match the form header (number + title), not body references like
  // "see Form 6251 for AMT calculation".
  const section = findSection(text, /Form\s*6251\b[\s\S]{0,300}?Alternative\s+Minimum\s+Tax/i);
  if (!section) return out;

  const line4 = findLineAmount(section, '4');
  if (line4 != null) out.amti = line4;

  const line1b = findLineAmount(section, '1b');
  if (line1b != null) out.amtRegularBase = line1b;

  // Line 2a: Schedule A line 7 taxes for an itemizer (the standard deduction
  // otherwise). The mapper takes it back out, since the engine adds SALT itself.
  const line2a = findLineAmount(section, '2a');
  if (line2a != null) out.line2a = line2a;

  // Legacy: synthetic test fixtures emit a single "2 ... amount" row.
  // Real IRS forms don't have a value next to bare "2" (it's a header),
  // so this returns junk on real PDFs — but the AMTI/line1b path above
  // takes precedence in the mapper.
  const line2 = findLineAmount(section, '2');
  if (line2 != null) out.amtAdjustment = line2;

  return out;
}

/**
 * Drop tax-software cover material that precedes the actual Form 1040.
 *
 * Vendors (Drake, Lacerte, ProConnect, UltraTax, TurboTax) routinely
 * prepend client letters, filing instructions, invoices, Form 8879
 * e-file authorizations, and two-year comparison summaries to the return
 * PDF. That material mentions "Form 1040", "Schedule A", the filing
 * status, SSNs, dates, and the PREPARER's address — so every first-match
 * heuristic in this file (1040 section scoping, schedule section scoping,
 * state-from-address, tax-year fallback, header SSN count) can lock onto
 * the cover pages instead of the return and come back empty or wrong.
 *
 * The real Form 1040 page 1 carries a fingerprint no cover page has: the
 * form number, the form title, and the "OMB No. 1545-0074" control number
 * in the same header band (verified against the IRS blank f1040.pdf and
 * the overlay fixtures in scripts/fixtures/sample-1040s). Find that band
 * and cut everything before the page it sits on. Pages are joined with
 * "\n\n" by extractPdfText, so the cut lands on a page boundary and the
 * 1040's own page stays intact.
 *
 * Returns the text unchanged when the fingerprint is missing (synthetic
 * fixtures without an OMB header, unusual vendors) or already on page 1.
 */
export function skipCoverPages(text: string): { text: string; skippedPages: number } {
  // All three parts must sit on the SAME page within a short span. A
  // client letter that says "your Form 1040 U.S. Individual Income Tax
  // Return" right before a Form 8879 page (which prints the same OMB
  // number) would otherwise satisfy a loose cross-page match. "\n\n" is
  // the page separator, so the gap pattern refuses to cross one.
  const anchorRx =
    /Form\s*1040\b(?:[^\n]|\n(?!\n)){0,150}?U\.?S\.?\s*Individual\s+Income\s+Tax\s+Return(?:[^\n]|\n(?!\n)){0,150}?OMB\s*No\.?\s*1545-0074/i;
  const m = anchorRx.exec(text);
  if (!m) {
    dbg('skipCoverPages: no Form 1040 header fingerprint found — text unchanged');
    return { text, skippedPages: 0 };
  }
  const pageBreak = text.lastIndexOf('\n\n', m.index);
  if (pageBreak < 0) return { text, skippedPages: 0 };
  const cut = pageBreak + 2;
  const prefix = text.slice(0, cut);
  const skippedPages = (prefix.match(/\n\n/g) ?? []).length;
  dbg(`skipCoverPages: dropped ${cut} chars / ${skippedPages} page(s) before the Form 1040`);
  return { text: text.slice(cut), skippedPages };
}

/**
 * Extractor: pure text-regex strategy. Produces a Parsed1040Return —
 * the architecture-neutral intermediate type that other extractors
 * (text-positional, IRS form-fields) will also produce.
 */
export function extractTextRegex(rawText: string): Parsed1040Return {
  const fullText = rawText || '';
  const extractedAt = new Date().toISOString();

  const looksLike1040 = /Form\s*1040|U\.?S\.?\s*Individual\s*Income\s*Tax\s*Return/i.test(fullText);

  if (!looksLike1040) {
    const trimmedLen = fullText.replace(/\s+/g, '').length;
    if (trimmedLen < 50) {
      return {
        sourceVendor: 'unknown',
        extractionMethod: 'text_regex',
        extractedAt,
        notRecognized: false,
        likelyScanned: true,
        warnings: [{
          field: 'document',
          severity: 'error',
          message: 'PDF appears to be scanned or has no text layer. Try a tax-software-generated PDF (TurboTax, Drake, Lacerte, ProConnect) or enter values manually.',
        }],
      };
    }
    return {
      sourceVendor: 'unknown',
      extractionMethod: 'text_regex',
      extractedAt,
      notRecognized: true,
      likelyScanned: false,
      warnings: [{
        field: 'document',
        severity: 'error',
        message: 'This PDF does not appear to be a Form 1040.',
      }],
    };
  }

  const out: Parsed1040Return = {
    sourceVendor: 'unknown',
    extractionMethod: 'text_regex',
    extractedAt,
    notRecognized: false,
    likelyScanned: false,
    warnings: [],
  };

  // Everything below reads from `text`, which starts at the Form 1040's
  // own page. Cover pages ahead of it are dropped so first-match
  // heuristics see the return, not the client letter.
  const { text, skippedPages } = skipCoverPages(fullText);
  if (skippedPages > 0) {
    out.warnings.push({
      field: 'document',
      severity: 'info',
      message: `Skipped ${skippedPages} cover page${skippedPages === 1 ? '' : 's'} (client letter, filing instructions, e-file authorization, etc.) ahead of the Form 1040.`,
    });
  }

  // ── Header / personal ────────────────────────────────────────────
  // Prefer the 1040's own header. If it carries no usable status marker,
  // fall back to the full document — tax-software cover pages often print
  // an explicit "Filing Status: Married Filing Jointly" line.
  const status = detectFilingStatus(text) ?? (skippedPages > 0 ? detectFilingStatus(fullText) : null);
  if (status) {
    out.filingStatus = field(status, '1040', 'header', 'high');
  } else {
    out.warnings.push({
      field: 'filingStatus',
      severity: 'warning',
      message: 'Could not auto-detect filing status — please confirm.',
    });
  }

  const yr = detectTaxYear(text);
  if (yr) out.taxYear = field(yr, '1040', 'header', 'high');

  const isJoint = status === 'MFJ';
  const nameParts = detectNames(text, isJoint);
  if (nameParts.taxpayerFirst) out.taxpayerFirstName = field(nameParts.taxpayerFirst, '1040', 'header', 'medium');
  if (nameParts.taxpayerLast) out.taxpayerLastName = field(nameParts.taxpayerLast, '1040', 'header', 'medium');
  if (nameParts.spouseFirst) out.spouseFirstName = field(nameParts.spouseFirst, '1040', 'header', 'medium');
  if (nameParts.spouseLast) out.spouseLastName = field(nameParts.spouseLast, '1040', 'header', 'medium');

  const stateAbbr = detectState(text);
  if (stateAbbr) out.addressState = field(stateAbbr, '1040', 'address', 'medium');

  const age = detectAge65(text);
  if (age.tp65Plus) out.taxpayer65Plus = field(true, '1040', 'header', 'high');
  if (age.sp65Plus) out.spouse65Plus = field(true, '1040', 'header', 'high');

  // ── Form 1040 lines ──────────────────────────────────────────────
  // `alts` lists alternate line IDs to try if the primary doesn't match.
  // The 2025 1040 renamed line 7 to "7a" (different from 2024 and earlier);
  // keep "7" as a fallback so older returns still parse.
  const lineMap: Array<{ line: string; alts?: string[]; key: keyof Parsed1040Return; keyword?: string }> = [
    { line: '1a', key: 'line1a_wages' },
    { line: '1z', key: 'line1z_totalWages' },
    { line: '2a', key: 'line2a_taxExemptInterest' },
    { line: '2b', key: 'line2b_taxableInterest' },
    { line: '3a', key: 'line3a_qualifiedDividends' },
    { line: '3b', key: 'line3b_ordinaryDividends' },
    { line: '4a', key: 'line4a_iraDistributions' },
    { line: '4b', key: 'line4b_iraTaxable' },
    { line: '5a', key: 'line5a_pensions' },
    { line: '5b', key: 'line5b_pensionsTaxable' },
    { line: '6a', key: 'line6a_socialSecurityGross' },
    { line: '6b', key: 'line6b_socialSecurityTaxable' },
    { line: '7a', alts: ['7'], key: 'line7_capitalGainOrLoss' },
    { line: '8', key: 'line8_otherIncomeSch1' },
    { line: '9', key: 'line9_totalIncome' },
    { line: '10', key: 'line10_adjustments' },
    { line: '11', key: 'line11_agi' },
    { line: '25a', key: 'line25a_w2Withholding' },
    { line: '25b', key: 'line25b_1099Withholding' },
    { line: '25c', key: 'line25c_otherWithholding' },
    { line: '25d', key: 'line25d_totalWithholding' },
    { line: '26', key: 'line26_estimatedPayments' },
  ];
  // Scope 1040 line searches to the 1040 form's text only. Without this,
  // looking for "5b" matches Schedule A line 5b (real estate taxes) or
  // looking for "3a" matches Schedule B's "Line 3a" instructional text.
  // Falls back to full text if 1040 section can't be isolated.
  const text1040 = findSection(
    text,
    /Form\s*1040[\s\S]{0,300}?U\.?S\.?\s*Individual\s+Income\s+Tax\s+Return/i,
  ) ?? text;
  for (const m of lineMap) {
    let v = findLineAmount(text1040, m.line, m.keyword);
    let usedLineId = m.line;
    if (v == null && m.alts) {
      for (const alt of m.alts) {
        v = findLineAmount(text1040, alt, m.keyword);
        if (v != null) { usedLineId = alt; break; }
      }
    }
    if (v != null) {
      (out as unknown as Record<string, ExtractedField<number>>)[m.key as string] =
        field<number>(v, '1040', usedLineId, 'medium');
    }
  }

  // ── Schedule A ───────────────────────────────────────────────────
  const schA = parseScheduleA(text);
  if (schA.medicalExpenses != null) out.schedAMedical = field(schA.medicalExpenses, 'Schedule A', '1');
  if (schA.saltStateLocal != null) out.schedASaltStateLocal = field(schA.saltStateLocal, 'Schedule A', '5a');
  if (schA.saltRealEstate != null) out.schedASaltRealEstate = field(schA.saltRealEstate, 'Schedule A', '5b');
  if (schA.saltPersonalProperty != null) out.schedASaltPersonalProperty = field(schA.saltPersonalProperty, 'Schedule A', '5c');
  if (schA.homeMortgageInterest != null) out.schedAMortgageInterest = field(schA.homeMortgageInterest, 'Schedule A', '8a');
  if (schA.investmentInterest != null) out.schedAInvestmentInterest = field(schA.investmentInterest, 'Schedule A', '9');
  if (schA.cashGiftsPublicCharity != null) out.schedACashCharity = field(schA.cashGiftsPublicCharity, 'Schedule A', '11');
  if (schA.nonCashPropertyPublicCharity != null) out.schedANonCashCharity = field(schA.nonCashPropertyPublicCharity, 'Schedule A', '12');
  if (schA.mortgageNotOn1098 != null) out.schedAMortgageNotOn1098 = field(schA.mortgageNotOn1098, 'Schedule A', '8b');
  if (schA.mortgagePoints != null) out.schedAPoints = field(schA.mortgagePoints, 'Schedule A', '8c');
  if (schA.otherItemizedDeductions != null) out.schedAOther = field(schA.otherItemizedDeductions, 'Schedule A', '16');

  // ── Schedule 1 Part II ───────────────────────────────────────────
  const sch1 = parseSchedule1(text);
  for (const [line, key] of SCHEDULE_1_LINES) {
    const amount = sch1.get(line);
    if (amount != null) {
      (out as unknown as Record<string, ExtractedField<number>>)[key as string] =
        field<number>(amount, 'Schedule 1', line);
    }
  }

  // ── Schedule C (taxpayer + optional spouse) ──────────────────────
  // Only a joint return has a spouse. On any other return every Schedule C
  // is the taxpayer's own business, so they are added together.
  const parsedSchC = parseScheduleC(text);
  const joint = out.filingStatus?.value === 'MFJ';
  const schCFilings: ScheduleCFiling[] = !joint && parsedSchC.length > 1
    ? [parsedSchC.reduce((sum, f) => ({
        grossReceipts: (sum.grossReceipts ?? 0) + (f.grossReceipts ?? 0),
        totalExpenses: (sum.totalExpenses ?? 0) + (f.totalExpenses ?? 0),
        fromNetProfit: sum.fromNetProfit || f.fromNetProfit,
      }), {} as ScheduleCFiling)]
    : parsedSchC;
  const expensesLine = (f: ScheduleCFiling) => (f.fromNetProfit ? '1 − 31' : '28');
  if (schCFilings.length >= 1) {
    const tp = schCFilings[0];
    if (tp.grossReceipts != null) out.schedCGrossReceipts = field(tp.grossReceipts, 'Schedule C', '1');
    if (tp.totalExpenses != null) out.schedCTotalExpenses = field(tp.totalExpenses, 'Schedule C', expensesLine(tp));
  }
  if (schCFilings.length >= 2) {
    const sp = schCFilings[1];
    if (sp.grossReceipts != null) out.spouseSchedCGrossReceipts = field(sp.grossReceipts, 'Schedule C (spouse)', '1');
    if (sp.totalExpenses != null) out.spouseSchedCTotalExpenses = field(sp.totalExpenses, 'Schedule C (spouse)', expensesLine(sp));
  }
  if (schCFilings.length > 2) {
    out.warnings.push({
      field: 'schedC',
      severity: 'warning',
      message: `Found ${schCFilings.length} Schedule C filings; only the first two were imported (taxpayer + spouse). Combine remaining manually.`,
    });
  }

  // ── Schedule SE (taxpayer + optional spouse) ─────────────────────
  const seFilings = parseScheduleSE(text);
  if (seFilings.length >= 1) {
    out.schedSETaxpayerFound = true;
    if (seFilings[0] != null) out.schedSETaxpayerW2SsWages = field(seFilings[0], 'Schedule SE', '8a');
  }
  if (seFilings.length >= 2) {
    out.schedSESpouseFound = true;
    if (seFilings[1] != null) out.schedSESpouseW2SsWages = field(seFilings[1], 'Schedule SE (spouse)', '8a');
  }

  // ── Schedule D ───────────────────────────────────────────────────
  const schD = parseScheduleD(text);
  if (schD.shortTerm != null) out.shortTermCapitalGains = field(schD.shortTerm, 'Schedule D', '7', 'high');
  if (schD.longTerm != null) out.longTermCapitalGains = field(schD.longTerm, 'Schedule D', '15', 'high');

  // Derive 1040 line 7 from Schedule D when the 1040 lookup missed it.
  // By IRS rule, 1040 line 7 = Schedule D line 16 = STCG + LTCG, so this
  // is a definition-based derivation, not a guess. Keeps the validator's
  // line-9 reconciliation accurate even when a vendor's PDF layout
  // breaks our direct 1040-line-7 extraction (e.g. the right-margin "7"
  // marker landing on a different y-row from the value, or the section
  // boundary cutting off the row).
  if (!out.line7_capitalGainOrLoss && (out.shortTermCapitalGains || out.longTermCapitalGains)) {
    const combined =
      (out.shortTermCapitalGains?.value ?? 0) +
      (out.longTermCapitalGains?.value ?? 0);
    out.line7_capitalGainOrLoss = field(combined, 'Schedule D', '16-derived', 'high');
  }

  // ── Schedule E ───────────────────────────────────────────────────
  const schE = parseScheduleE(text);
  if (schE.rentalIncome != null) out.rentalIncome = field(schE.rentalIncome, 'Schedule E', '23a');
  if (schE.rentalExpenses != null) out.rentalExpenses = field(schE.rentalExpenses, 'Schedule E', '23e');
  if (schE.k1Ordinary != null) out.k1OrdinaryBusinessIncome = field(schE.k1Ordinary, 'Schedule E', '28');
  if (schE.k1Guaranteed != null) out.k1GuaranteedPayments = field(schE.k1Guaranteed, 'Schedule E', '28');
  if (schE.k1RentalRealEstate != null) out.k1RentalRealEstateIncome = field(schE.k1RentalRealEstate, 'Schedule E', '28');
  if (schE.k1Other != null) out.k1OtherIncome = field(schE.k1Other, 'Schedule E', '28');

  // ── Form 8995-A ──────────────────────────────────────────────────
  const qbi = parseForm8995A(text);
  if (qbi.qbiW2Wages != null) out.qbiW2Wages = field(qbi.qbiW2Wages, 'Form 8995-A', '4');
  if (qbi.qbiUBIA != null) out.qbiUBIA = field(qbi.qbiUBIA, 'Form 8995-A', '7');
  if (qbi.qbiSSTB) out.qbiSSTB = field(true, 'Form 8995-A', 'I', 'medium');

  // ── Form 6251 ────────────────────────────────────────────────────
  const amt = parseForm6251(text);
  if (amt.amtAdjustment != null) out.amtAdjustment = field(amt.amtAdjustment, 'Form 6251', '2');
  if (amt.amti != null) out.amti = field(amt.amti, 'Form 6251', '4', 'high');
  if (amt.amtRegularBase != null) out.amtRegularBase = field(amt.amtRegularBase, 'Form 6251', '1b', 'high');
  if (amt.line2a != null) out.amtLine2a = field(amt.line2a, 'Form 6251', '2a');

  return out;
}

/**
 * Legacy entry point — runs the text-regex extractor through the validator
 * and the wizard mapper. Kept for backward compatibility with existing
 * callers and tests; new code should use extractTextRegex + mapToWizardInput
 * directly.
 */
export function parse1040Text(rawText: string): Parsed1040 {
  const parsed = extractTextRegex(rawText);
  // Append cross-validation warnings to the structured warnings array.
  parsed.warnings.push(...validate1040Return(parsed));
  const mapped = mapToWizardInput(parsed);
  return {
    fields: mapped.fields,
    warnings: mapped.warnings,
    wageSplitTotal: mapped.wageSplitTotal,
    extracted: mapped.extracted,
    notRecognized: parsed.notRecognized,
    likelyScanned: parsed.likelyScanned,
  };
}
